import { useState, useEffect } from 'react'
import { MerchantLayout } from '../../components/layouts/Layout'
import { useAuth } from '../../hooks/useAuth'
import { Card, Button, Badge, Modal } from '../../components/common/UI'
import { BusinessForm } from '../../components/forms/BusinessForm'
import { Plus, Edit, Building, Mail, Phone, ShieldCheck, ShieldAlert, AlertCircle } from 'lucide-react'
import { formatters } from '../../utils/formatting'
import { businessService, merchantService } from '../../services/api'

export default function Businesses() {
  const { user } = useAuth()
  const [businesses, setBusinesses] = useState<any[]>([])
  const [merchant, setMerchant] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingBusiness, setEditingBusiness] = useState<any>(null)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [verifyModal, setVerifyModal] = useState<{ open: boolean; type: 'email' | 'phone' | null; value: string; businessId: string }>({ open: false, type: null, value: '', businessId: '' })
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState('')
  const [verifyLoading, setVerifyLoading] = useState(false)

  useEffect(() => {
    if (user?.merchantId) {
      loadData()
    }
  }, [user?.merchantId])

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const loadData = async () => {
    if (!user?.merchantId) return
    
    setLoading(true)
    try {
      const [bizData, merchantData] = await Promise.all([
        businessService.listByMerchant(user.merchantId),
        merchantService.get(user.merchantId)
      ])
      setBusinesses(bizData)
      setMerchant(merchantData)
    } catch (error) {
      console.error('Failed to load businesses:', error)
    } finally {
      setLoading(false)
    }
  }

  const canCreateBusiness = () => {
    return merchant?.emailVerified || merchant?.phoneVerified
  }

  const handleAddBusiness = () => {
    if (!canCreateBusiness()) {
      alert('Please verify your email or phone number in your profile before creating a business')
      return
    }
    setShowModal(true)
  }

  const handleSubmit = async (data: any) => {
    if (!canCreateBusiness()) {
      alert('Please verify your account before creating a business')
      return
    }

    setSubmitLoading(true)
    try {
      if (editingBusiness) {
        await businessService.update(editingBusiness.id, data)
      } else {
        await businessService.create({
          ...data,
          merchantId: user?.merchantId,
          verificationStatus: 'PENDING',
          status: 'ACTIVE',
          emailVerified: false,
          phoneVerified: false
        })
      }
      
      setShowModal(false)
      setEditingBusiness(null)
      await loadData()
    } catch (error) {
      throw error
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleEdit = (business: any) => {
    setEditingBusiness(business)
    setShowModal(true)
  }

  const handleCancel = () => {
    setShowModal(false)
    setEditingBusiness(null)
  }

  const handleOpenVerify = (type: 'email' | 'phone', value: string, businessId: string) => {
    setVerifyModal({ open: true, type, value, businessId })
    setOtp('')
    setOtpSent(false)
    setError('')
    setCountdown(0)
  }

  const handleSendOTP = async () => {
    setVerifyLoading(true)
    setError('')
    
    try {
      await businessService.sendOTP(verifyModal.value)
      setOtpSent(true)
      setCountdown(60)
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP')
    } finally {
      setVerifyLoading(false)
    }
  }

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setError('Please enter a 6-digit OTP')
      return
    }

    setVerifyLoading(true)
    setError('')

    try {
      const isValid = await businessService.verifyOTP(verifyModal.value, otp)
      
      if (isValid) {
        await businessService.updateVerification(verifyModal.businessId, verifyModal.type!)
        setVerifyModal({ open: false, type: null, value: '', businessId: '' })
        setOtp('')
        setOtpSent(false)
        await loadData()
      } else {
        setError('Invalid OTP. Please try again.')
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed')
    } finally {
      setVerifyLoading(false)
    }
  }

  if (loading) {
    return (
      <MerchantLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      </MerchantLayout>
    )
  }

  return (
    <MerchantLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">My Businesses</h1>
            <p className="text-gray-600">Manage your business listings</p>
          </div>
          <Button 
            onClick={handleAddBusiness} 
            icon={Plus}
            disabled={!canCreateBusiness()}
          >
            Add Business
          </Button>
        </div>

        {!canCreateBusiness() && (
          <Card className="bg-yellow-50 border-yellow-200">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900">Verification Required</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Please verify your email or phone number in your profile before creating businesses.
                </p>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => window.location.href = '/merchant/profile'}>
                  Go to Profile
                </Button>
              </div>
            </div>
          </Card>
        )}

        {businesses.some(b => !b.emailVerified || !b.phoneVerified) && (
          <Card className="bg-blue-50 border-blue-200">
            <div className="flex items-start space-x-3">
              <Building className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">Verify Your Business Contacts</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Verified email and phone numbers are required to create advertisements and help speed up admin approval.
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {businesses.map((business) => (
            <Card key={business.id} className="flex flex-col hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold truncate">{business.businessName}</h3>
                      {business.emailVerified && business.phoneVerified && (
                        <div title="Fully Verified">
                          <ShieldCheck className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {Array.isArray(business.businessType) ? business.businessType.join(', ') : business.businessType}
                    </p>
                  </div>
                </div>
                <Badge variant={
                  business.verificationStatus === 'VERIFIED' ? 'success' :
                  business.verificationStatus === 'PENDING' ? 'warning' : 'error'
                }>
                  {business.verificationStatus}
                </Badge>
              </div>

              <div className="space-y-2 text-sm mb-4 flex-1">
                <div className="flex">
                  <span className="text-gray-600 w-20 flex-shrink-0">Contact:</span>
                  <span className="font-medium truncate">{business.contactPerson}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 truncate text-xs">{business.businessEmail}</span>
                  </div>
                  {business.emailVerified ? (
                    <Badge variant="success" className="text-xs px-2 py-0.5">
                      <ShieldCheck className="w-3 h-3" />
                    </Badge>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs px-2 py-1"
                      onClick={() => handleOpenVerify('email', business.businessEmail, business.id)}
                    >
                      Verify
                    </Button>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 flex-1">
                    <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 text-xs">{business.phoneNumber}</span>
                  </div>
                  {business.phoneVerified ? (
                    <Badge variant="success" className="text-xs px-2 py-0.5">
                      <ShieldCheck className="w-3 h-3" />
                    </Badge>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-xs px-2 py-1"
                      onClick={() => handleOpenVerify('phone', business.phoneNumber, business.id)}
                    >
                      Verify
                    </Button>
                  )}
                </div>

                {business.location?.address && (
                  <div className="flex">
                    <span className="text-gray-600 w-20 flex-shrink-0">Address:</span>
                    <span className="font-medium line-clamp-2">{business.location.address}</span>
                  </div>
                )}

                {business.website && (
                  <div className="flex">
                    <span className="text-gray-600 w-20 flex-shrink-0">Website:</span>
                    <a 
                      href={business.website} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="font-medium text-blue-600 hover:underline truncate"
                    >
                      {business.website}
                    </a>
                  </div>
                )}

                <div className="flex">
                  <span className="text-gray-600 w-20 flex-shrink-0">Created:</span>
                  <span className="font-medium">{formatters.date(business.createdAt)}</span>
                </div>
              </div>

              {(!business.emailVerified || !business.phoneVerified) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                  <p className="text-xs text-yellow-800">
                    ⚠️ Verify both contacts to create advertisements
                  </p>
                </div>
              )}

              <Button
                size="sm"
                variant="outline"
                className="w-full mt-auto"
                icon={Edit}
                onClick={() => handleEdit(business)}
              >
                Edit Business
              </Button>
            </Card>
          ))}
        </div>

        {businesses.length === 0 && canCreateBusiness() && (
          <Card className="text-center py-12">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Businesses Yet</h3>
            <p className="text-gray-600 mb-4">Add your first business to start advertising</p>
            <Button onClick={handleAddBusiness} icon={Plus}>
              Add Business
            </Button>
          </Card>
        )}

        <Modal
          isOpen={showModal}
          onClose={handleCancel}
          title={editingBusiness ? 'Edit Business' : 'Add Business'}
          size="lg"
        >
          <BusinessForm
            business={editingBusiness}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={submitLoading}
          />
        </Modal>

        <Modal
          isOpen={verifyModal.open}
          onClose={() => setVerifyModal({ open: false, type: null, value: '', businessId: '' })}
          title={`Verify ${verifyModal.type === 'email' ? 'Email' : 'Phone Number'}`}
          size="md"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
              {verifyModal.type === 'email' ? (
                <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              ) : (
                <Phone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className="font-medium text-blue-900">
                  {verifyModal.type === 'email' ? 'Email' : 'Phone'}: {verifyModal.value}
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  We'll send a 6-digit verification code to this {verifyModal.type === 'email' ? 'email address' : 'phone number'}.
                </p>
              </div>
            </div>

            {!otpSent ? (
              <Button onClick={handleSendOTP} loading={verifyLoading} className="w-full">
                Send Verification Code
              </Button>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Enter 6-digit code
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '')
                      setOtp(value)
                      setError('')
                    }}
                    placeholder="000000"
                    className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                    <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex space-x-3">
                  <Button
                    onClick={handleVerifyOTP}
                    loading={verifyLoading}
                    disabled={otp.length !== 6}
                    className="flex-1"
                  >
                    Verify Code
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={handleSendOTP}
                    disabled={countdown > 0 || verifyLoading}
                    className="flex-1"
                  >
                    {countdown > 0 ? `Resend (${countdown}s)` : 'Resend Code'}
                  </Button>
                </div>

                <p className="text-xs text-gray-500 text-center">
                  Didn't receive the code? Check your spam folder or try resending.
                </p>
              </>
            )}
          </div>
        </Modal>
      </div>
    </MerchantLayout>
  )
}